/* eslint-disable import/no-extraneous-dependencies */
import React, { useState, useRef, useEffect } from 'react';
import Model from '@tripian/model';
import classes from './KiwiHotelFilterBar.scss';

export type KiwiSortOption = '' | 'price_low_to_high' | 'price_high_to_low' | 'recently_added' | 'star_rating' | 'average_rating' | 'alphabetical';

interface IKiwiHotelFilterBar {
  sortBy: KiwiSortOption;
  onSortChange: (sort: KiwiSortOption) => void;
  onShowMapToggle?: () => void;
  onFilterToggle?: () => void;
  showMap?: boolean;
  resultCount?: number;
  t: (value: Model.TranslationKey) => string;
}

const SORT_OPTIONS: { value: KiwiSortOption; tKey: Model.TranslationKey }[] = [
  { value: '', tKey: 'kiwi.filterBar.sort.all' },
  { value: 'price_low_to_high', tKey: 'kiwi.filterBar.sort.priceLow' },
  { value: 'price_high_to_low', tKey: 'kiwi.filterBar.sort.priceHigh' },
  { value: 'recently_added', tKey: 'kiwi.filterBar.sort.recentlyAdded' },
  { value: 'star_rating', tKey: 'kiwi.filterBar.sort.starRating' },
  { value: 'average_rating', tKey: 'kiwi.filterBar.sort.averageRating' },
  { value: 'alphabetical', tKey: 'kiwi.filterBar.sort.alphabetical' },
];

const KiwiHotelFilterBar: React.FC<IKiwiHotelFilterBar> = ({ sortBy, onSortChange, onShowMapToggle, onFilterToggle, showMap, resultCount, t }) => {
  const [sortOpen, setSortOpen] = useState(false);
  const sortRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (sortRef.current && !sortRef.current.contains(e.target as Node)) {
        setSortOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div className={classes.filterBar}>
      {/* Left — View on Map pill */}
      <div className={`${classes.left} ${showMap ? classes.leftActive : ''}`} onClick={onShowMapToggle} role="button" tabIndex={0} onKeyDown={() => {}}>
        <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
          <path d="M20.5 3l-.16.03L15 5.1 9 3 3.36 4.9c-.21.07-.36.25-.36.48V20.5c0 .28.22.5.5.5l.16-.03L9 18.9l6 2.1 5.64-1.9c.21-.07.36-.25.36-.48V3.5c0-.28-.22-.5-.5-.5zM15 19l-6-2.11V5l6 2.11V19z" />
        </svg>
        <span>{t('kiwi.filterBar.viewOnMap')}</span>
      </div>

      {/* Right — Sort by + Filter */}
      <div className={classes.right}>
        {/* Sort by */}
        <div className={classes.sortWrapper} ref={sortRef}>
          <div className={classes.actionBtn} onClick={() => setSortOpen(!sortOpen)} role="button" tabIndex={0} onKeyDown={() => {}}>
            <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
              <path d="M16 17.01V10h-2v7.01h-3L15 21l4-3.99h-3zM9 3L5 6.99h3V14h2V6.99h3L9 3z" />
            </svg>
            <span>{t('kiwi.filterBar.sortBy')}</span>
          </div>
          {sortOpen && (
            <div className={classes.sortDropdown}>
              <div className={classes.sortHeader}>{t('kiwi.filterBar.sortBy')}</div>
              <div className={classes.sortDivider} />
              <div className={classes.sortList}>
                {SORT_OPTIONS.map((option) => (
                  <div
                    key={option.value}
                    className={classes.sortOption}
                    onClick={() => {
                      onSortChange(option.value);
                      setSortOpen(false);
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={() => {}}
                  >
                    <span className={`${classes.radio} ${sortBy === option.value ? classes.radioActive : ''}`} />
                    <span>{t(option.tKey)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Filter */}
        {onFilterToggle && (
          <div className={classes.actionBtn} onClick={onFilterToggle} role="button" tabIndex={0} onKeyDown={() => {}}>
            <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
              <path d="M3 17v2h6v-2H3zM3 5v2h10V5H3zm10 16v-2h8v-2h-8v-2h-2v6h2zM7 9v2H3v2h4v2h2V9H7zm14 4v-2H11v2h10zm-6-4h2V7h4V5h-4V3h-2v6z" />
            </svg>
            <span>{t('kiwi.filterBar.filter')}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default KiwiHotelFilterBar;
